import { useSyncExternalStore, useCallback } from 'react';
import { capture } from '../lib/posthog';

const KEY = 'melodiza:liked-playlists'; // distinct from melodiza:liked (tracks)

// Module-level shared state — all consumers re-render on toggle
let likedSet = (() => {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) || '[]'));
  } catch {
    return new Set();
  }
})();

const listeners = new Set();

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return likedSet;
}

function emit() {
  listeners.forEach((l) => l());
}

function setLiked(updater) {
  const next = updater(likedSet);
  likedSet = next;
  localStorage.setItem(KEY, JSON.stringify([...next]));
  emit();
}

// Cross-tab sync (storage event fires in OTHER tabs)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    try {
      likedSet = new Set(JSON.parse(e.newValue || '[]'));
      emit();
    } catch {
      // ignore corrupted state
    }
  });
}

// ─── Test helper ───────────────────────────────────────────────────────

export function __resetForTests() {
  try {
    likedSet = new Set(JSON.parse(localStorage.getItem(KEY) || '[]'));
  } catch {
    likedSet = new Set();
  }
  listeners.clear();
}

export function useLikedPlaylists() {
  const liked = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const toggle = useCallback((playlistId, source = 'unknown') => {
    const isLikedNow = likedSet.has(playlistId);
    capture(isLikedNow ? 'playlist_unliked' : 'playlist_liked', {
      playlistId,
      source,
    });
    setLiked((prev) => {
      const next = new Set(prev);
      if (isLikedNow) next.delete(playlistId);
      else next.add(playlistId);
      return next;
    });
  }, []);

  const isLiked = useCallback((id) => liked.has(id), [liked]);

  return { liked, toggle, isLiked };
}
